import CircleIcon from "@mui/icons-material/Circle";
import {TreeItem2 as TreeItem} from "@mui/x-tree-view";
import React, {useContext} from "react";
import type {ReactNode} from "react";

import AssessmentDeliveryContext, {
	AssessmentDeliveryContextProvider,
	SessionAssessmentDeliveryContextProvider,
} from "../AssessmentDeliveryContext";
import CustomTreeItem from "./CustomTreeItem";
import type LabelProps from "./LabelProps";
import SectionNavItem from "./SectionNavItem";
import type Chapter from "../../store/chapters/Chapter";
import {requiresExplicitSessionStart} from "../../store/chapters/Chapter";
import selectChapter from "../../store/chapters/selectChapter";
import selectChapterSessionKeys from "../../store/chapterSessions/selectChapterSessionKeys";
import selectCourseType from "../../store/courses/selectCourseType";
import {useAppSelector} from "../../store/hooks";
import {selectUserId} from "../../store/userProfile/selectUserProfile";

function ChapterNavItem(props: {
	courseId: number;
	chapterKey: string;

	collapsible?: boolean;

	renderChapterLabel: (
		labelType: "disabled" | "expandable" | "unexpandable",
		params: LabelProps
	) => React.ReactNode;
	renderSectionLabel: (
		sectionKey: string,
		params: LabelProps
	) => React.ReactNode;
	renderExerciseLabel: (
		sectionKey: string,
		exerciseId: number,
		params: LabelProps
	) => React.ReactNode;

	defineItemType: (
		chapter: Chapter
	) => {
		clickable: boolean;
		disabled: boolean;
		expandable: boolean;
		highlighted?: boolean;
	};
	defineSectionItemType?: (
		sectionKey: string
	) => {
		highlighted?: boolean;
	};
	defineExerciseItemType?: () => {
		clickable: boolean;
	};
}) {
	const {chapterKey, defineItemType, courseId} = props;

	const chapter = useAppSelector((state) => selectChapter(state, chapterKey));

	const sessionKey = useAppSelector((state) => {
		const [sessions] = selectChapterSessionKeys(state, chapterKey);
		return sessions.length > 0 ? sessions[0] : "";
	});

	const studentId = useAppSelector(selectUserId);

	const courseType = useAppSelector((state) =>
		selectCourseType(state, courseId)
	);

	if (!chapter || !courseType) {
		return <></>;
	}

	const {clickable, disabled, expandable, highlighted} = defineItemType(
		chapter
	);

	if (disabled || chapter.structureType !== "sections") {
		return (
			<CustomTreeItem
				itemId={chapterKey}
				disabled
				label={props.renderChapterLabel("disabled", {
					index: `${chapter.number}`,
					text: chapter.title,
				})}
			/>
		);
	}

	if (!expandable) {
		return (
			<UnexpandableChapterNavItem
				chapterKey={chapterKey}
				label={props.renderChapterLabel("unexpandable", {
					index: `${chapter.number}`,
					text: chapter.title,
				})}
			/>
		);
	}

	const sessionRequired = requiresExplicitSessionStart(courseType, chapter);

	const navItem = (
		<ExpandableChapterNavItem
			courseId={courseId}
			chapterKey={chapterKey}
			label={props.renderChapterLabel("expandable", {
				index: `${chapter.number}`,
				text: chapter.title,
			})}
			collapsible={props.collapsible}
			highlighted={highlighted}
			renderSectionLabel={props.renderSectionLabel}
			renderExerciseLabel={props.renderExerciseLabel}
			clickable={clickable}
			defineSectionItemType={props.defineSectionItemType}
			defineExerciseItemType={props.defineExerciseItemType}
		/>
	);

	return sessionRequired ? (
		<SessionAssessmentDeliveryContextProvider
			sessionKey={sessionKey}
			courseId={courseId}
			chapterId={chapter.id}
			studentId={studentId}
		>
			{navItem}
		</SessionAssessmentDeliveryContextProvider>
	) : (
		<AssessmentDeliveryContextProvider
			courseId={courseId}
			chapterId={chapter.id}
			studentId={studentId}
		>
			{navItem}
		</AssessmentDeliveryContextProvider>
	);
}

function ExpandableChapterNavItem(props: {
	courseId: number;
	chapterKey: string;
	onClick?: () => void;

	label: ReactNode;

	collapsible?: boolean;
	highlighted?: boolean;

	renderSectionLabel: (
		sectionKey: string,
		params: LabelProps
	) => React.ReactNode;
	renderExerciseLabel: (
		sectionKey: string,
		exerciseId: number,
		params: LabelProps
	) => React.ReactNode;

	defineSectionItemType?: (
		sectionKey: string
	) => {
		highlighted?: boolean;
	};
	defineExerciseItemType?: () => {
		clickable: boolean;
	};

	clickable: boolean;
}) {
	const {onClick, chapterKey, courseId, clickable} = props;

	const {
		fetchContent,
		fetchOutcomes,
		selectContentStatus,
		selectSectionKeys,
	} = useContext(AssessmentDeliveryContext);

	const sections = useAppSelector(selectSectionKeys);

	const contentRequested = useAppSelector(
		(state) => selectContentStatus(state) !== "none"
	);

	function click() {
		if (!contentRequested) {
			fetchContent();
			fetchOutcomes();
		}
		onClick?.();
	}

	let icons;
	if (!props.collapsible) {
		icons = noIconSlots;
	}

	return (
		<CustomTreeItem
			itemId={chapterKey}
			label={props.label}
			onClick={click}
			slots={icons}
			slotProps={{
				iconContainer: {style: {marginTop: `calc(0.75rem - 9px)`}},
			}}
			classes={{content: "chapterItemContent"}}
			clickable={clickable}
			highlighted={props.highlighted}
			sx={[
				!clickable && {
					"& .chapterItemContent:hover": {
						background: "inherit",
					},
				},
			]}
		>
			{/* this line below is necessary for rendering expand icon */}
			{sections.length === 0 && <TreeItem itemId="empty" />}

			{sections.map((key: string, i: number) => (
				<SectionNavItem
					key={key}
					sectionKey={key}
					index={`${i + 1}`}
					courseId={courseId}
					renderLabel={props.renderSectionLabel}
					renderExerciseLabel={(exerciseId, params) =>
						props.renderExerciseLabel(key, exerciseId, params)
					}
					defineItemType={props.defineSectionItemType}
					defineExerciseItemType={props.defineExerciseItemType}
				/>
			))}
		</CustomTreeItem>
	);
}

const noIconSlots = {collapseIcon: NoIcon, expandIcon: NoIcon};

function NoIcon() {
	return <></>;
}

function UnexpandableChapterNavItem(props: {
	chapterKey: string;
	label: ReactNode;
	onClick?: () => void;
}) {
	const {chapterKey, onClick} = props;

	return (
		<CustomTreeItem
			itemId={chapterKey}
			label={props.label}
			onClick={onClick}
			slots={{icon: ItemIcon}}
			slotProps={{iconContainer: {style: {marginTop: `calc(0.75rem - 4px)`}}}}
		/>
	);
}

function ItemIcon() {
	return <CircleIcon color="primary" style={{fontSize: 8}} />;
}

export default ChapterNavItem;
